import { AiAgentAndPipelineResultsType, useAiAgentAndPipelineResults } from "./react.intent";
import React, { useState } from "react";
import { useSearchTextFilter } from "@me8eon/searchtext_filters";
import { ContextAndMessagesAndSelectedAndCard, PipelineDetailsData } from "@me8eon/agent";
import { ErrorsOr, isErrors, LogAnd } from "@me8eon/errors";
import { useCommonComponents } from "@me8eon/components";
import { SimpleSelectableButtonBar } from "@me8eon/selection_button";
import { useSelectedDataViewName } from "@me8eon/data_view_filter";
import { useRenderers } from "@me8eon/renderers";
import { useQuestionSuggestions } from "@me8eon/async";
import { AssistantMessageRequest, isAssistantMessageRequest, Message } from "@me8eon/messages";
import { ellipses } from "me-at-eon/src/util/strings";
import {useAgentContext} from "./context/AgentContext";

export const intentModes = ["summary", "json", "suggestions"];
export type SelectedDebugProps = {
    selected?: LogAnd<ErrorsOr<ContextAndMessagesAndSelectedAndCard<any>>>
}

export function SelectedDebug({ selected }: SelectedDebugProps) {
    const { ClipHeight } = useCommonComponents();
    return <ClipHeight maxHeight="500px" scrollable>
        <pre>{JSON.stringify(selected, null, 2)}</pre>
    </ClipHeight>;
}

export type PipelineDebugProps = {
    pipeline?: LogAnd<ErrorsOr<PipelineDetailsData<any>>>
}

export type AiMessageProps = {
    m: Message
}

export type AssistantMessageRequestProps = {
    m: AssistantMessageRequest
}

export function ShowAssistantMessageRequest({ m }: AssistantMessageRequestProps) {
    return <div style={{ border: "1px solid #ccc", borderRadius: "5px", padding: "10px", marginBottom: "10px", backgroundColor: "#e6f7ff" }}>
        <strong>{m.role}</strong>:
        {m.toolCalls && m.toolCalls.length > 0 && <div style={{ marginTop: "10px", padding: "10px", border: "1px dashed #ccc", borderRadius: "5px", backgroundColor: "#f0f5ff" }}>
            <strong>Tool Calls:</strong>
            <ul>
                {m.toolCalls.map((tc, i) => <li key={i}>{tc.tool_name}/{tc.tool_call_id} ({JSON.stringify(tc.tool_args)})</li>)}
            </ul>
        </div>}
    </div>;
}

export type ToolMessageProps = {
    m: Message
}

export function ShowToolMessage({ m }: ToolMessageProps) {
    return <div style={{ border: "1px solid #ccc", borderRadius: "5px", padding: "10px", marginBottom: "10px", backgroundColor: "#fff7e6" }}>
        <strong>{m.role}</strong> (Tool Call ID: {(m as any).tool_call_id}): {ellipses(m.content)}
    </div>;
}

export function AiMessage({ m }: AiMessageProps) {
    if (isAssistantMessageRequest(m)) return <ShowAssistantMessageRequest m={m} />;
    else if (m.role === "tool") return <ShowToolMessage m={m} />;
    else
        return <div style={{ border: "1px solid #ccc", borderRadius: "5px", padding: "10px", marginBottom: "10px", backgroundColor: "#f9f9f9" }}>
            <strong>{m.role}</strong>: {ellipses(m.content)}
        </div>;
}


export function PipelineDebug({ pipeline }: PipelineDebugProps) {
    const { ClipHeight } = useCommonComponents();
    if (!pipeline) return <div>No pipeline executed</div>;
    if (isErrors(pipeline))
        return <pre>{JSON.stringify(pipeline, null, 2)}</pre>;
    const appendMessages = pipeline.value?.appendMessages;
    if (!appendMessages || appendMessages.length === 0) return <div>No messages appended by pipeline</div>;
    return <ClipHeight maxHeight="500px" scrollable>{appendMessages?.map((m, i) => <AiMessage key={i} m={m} />)}</ClipHeight>;
}

type SummaryProps = { data: AiAgentAndPipelineResultsType<any> }

function SuccessSummary({ data }: SummaryProps) {
    const { query, agentResult, pipeline, errors } = data;
    const { ClipHeight } = useCommonComponents();
    const selectedName = agentResult ? (isErrors(agentResult) ? agentResult : agentResult.value?.selected) : "";
    const appendMessages = pipeline ? (isErrors(pipeline) ? pipeline : pipeline.value?.appendMessages) : "";
    return <ClipHeight maxHeight="400px" scrollable>
        <table
            style={{
                borderCollapse: "collapse",
                width: "100%",
                fontFamily: "sans-serif",
                fontSize: "14px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            }}
        >
            <thead>
            <tr style={{ backgroundColor: "#f4f4f4", textAlign: "left" }}>
                <th style={{ padding: "12px", borderBottom: "2px solid #ddd" }}>
                    Query
                </th>
                <th style={{ padding: "12px", borderBottom: "2px solid #ddd" }}>
                    Selected Agent
                </th>
                <th style={{ padding: "12px", borderBottom: "2px solid #ddd" }}>
                    Append Messages
                </th>
            </tr>
            </thead>
            <tbody>
            <tr style={{ backgroundColor: "#fff" }}>
                <td style={{ padding: "12px", borderBottom: "1px solid #eee" }}>
                    {query}
                </td>
                <td style={{ padding: "12px", borderBottom: "1px solid #eee" }}>
                    {typeof selectedName === "string"
                        ? selectedName
                        : JSON.stringify(selectedName)}
                </td>
                <td style={{ padding: "12px", borderBottom: "1px solid #eee" }}>
                    {typeof appendMessages === "string" ? appendMessages : ellipses(JSON.stringify(appendMessages), 200)}
                </td>
            </tr>
            </tbody>
        </table>
    </ClipHeight>;

}

export function IntentSummary() {
    const { TwoColumnAndRest, ClipHeight } = useCommonComponents();
    const [agentAndPipelineResults, setAgentAndPipelineResults] = useAiAgentAndPipelineResults();
    const { Json } = useRenderers();
    return <> <SuccessSummary data={agentAndPipelineResults} />
        <TwoColumnAndRest>
            <SelectedDebug selected={agentAndPipelineResults.agentResult} />
            <PipelineDebug pipeline={agentAndPipelineResults.pipeline} />
            {agentAndPipelineResults.errors && <ClipHeight maxHeight="200px" scrollable><Json rootId="intent.errors" attribute="intent.errors" value={agentAndPipelineResults.errors} /></ClipHeight>}
        </TwoColumnAndRest>
    </>;
}

export function IntentDevMode() {
    const intent = useAgentContext();
    const [query] = useSearchTextFilter();
    const intentOps = useState(intentModes[0]);
    const selectedIntent = intentOps[0];

    const { Json } = useRenderers();
    const { ClipHeight } = useCommonComponents();
    const [selected] = useSelectedDataViewName();
    const [agentAndPipelineResults] = useAiAgentAndPipelineResults();
    const [suggestions] = useQuestionSuggestions();
    return <div>
        <SimpleSelectableButtonBar options={intentModes} selectedOps={intentOps} rootId="intent.mode" />
        {selectedIntent === "summary" && <IntentSummary />}
        {selectedIntent === "json" && <ClipHeight maxHeight="500px" scrollable><Json rootId="intent.json" attribute="intent.all" value={agentAndPipelineResults} /></ClipHeight>}
        {selectedIntent === "suggestions" && <pre>{JSON.stringify(suggestions, null, 2)}</pre>}
        <pre>{JSON.stringify(selected, null, 2)}</pre>
    </div>;

}